/**
 * LINKURIOUS CONFIDENTIAL
 * Copyright Linkurious SAS 2012 - 2016
 *
 * Created by maximeallex on 2016-10-28.
 */


'use strict';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild} from '@angular/core';

import {OnClosingTooltip, OnOpeningTooltip, OnUpdateTooltip, TooltipService} from './service';
import {SafeHtml} from "@angular/platform-browser";
import {Tools} from "linkurious-shared";
import {TruncatePipe} from "../truncation/pipe";

@Component({
  selector   : 'lk-tooltip',
  template: `<div class="tooltip_curtain" [ngStyle]="location">
  <div #tooltip class="tooltip"
       (click)="copyTooltip()"
       id="tooltip_content"
       [style.maxWidth]="maxWidth"
       [ngClass]="{
        'tooltip-active': isShown,
        'tooltip-copy': copy,
        'tooltip-copied': copied
       }">
    {{content}}
    <div [class.none]="!copy || copied" class="copy">Click to copy</div>
    <div [class.none]="!copied" class="copied"><icon name="check" size="12"></icon> Copied</div>
  </div>
  <div class="arrow {{arrowPosition}}"
       [class.arrow_active]="isShown"
       [class.arrow_copied]="copied === true"
       [ngStyle]="arrowLocation"></div>
</div>

`,
  styles: [`.tooltip_curtain{position:absolute;padding:10px;z-index:9999}.tooltip{box-sizing:border-box;background:#363636;color:#fff;padding:12px 15px;font-size:12px;border-radius:3px;visibility:hidden;opacity:0;line-height:14px;transition:all ease-in-out .3s;word-wrap:break-word;-webkit-hyphens:auto;-ms-hyphens:auto;hyphens:auto;box-shadow:0 1px 3px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.24);-webkit-box-shadow:0 1px 3px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.24);-moz-box-shadow:0 1px 3px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.24);-o-box-shadow:0 1px 3px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.24)}.tooltip:first-letter{text-transform:uppercase}.tooltip-active{visibility:visible;opacity:.9;transition:opacity ease-in-out .3s}.tooltip-copy{cursor:pointer}.tooltip-copied{background:#0081e3;transition:all ease-in-out .2s}.arrow{visibility:hidden;opacity:0;position:absolute;width:0;height:0;transition:border-color,opacity ease-in-out .3s}.arrow.arrow_active{visibility:visible;opacity:.9;transition:opacity ease-in-out .3s}.arrow.arrow_copied.bottom{border-top:5px solid #0081e3!important}.arrow.arrow_copied.top{border-bottom:5px solid #0081e3!important}.arrow.arrow_copied.left{border-right:5px solid #0081e3!important}.arrow.arrow_copied.right{border-left:5px solid #0081e3!important}.arrow.bottom{border-top:5px solid #363636;border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:none}.arrow.top{border-top:none;border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:5px solid #363636}.arrow.left{border-top:5px solid transparent;border-left:none;border-right:5px solid #363636;border-bottom:5px solid transparent}.arrow.right{border-top:5px solid transparent;border-left:5px solid #363636;border-right:none;border-bottom:5px solid transparent}.copy{margin-top:10px;text-align:right;font-size:10px;text-decoration:underline}.copied{margin-top:10px;font-size:10px;display:flex;align-items:center;justify-content:flex-end;width:100%}`],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TooltipComponent {

  @ViewChild('tooltip') tooltip;
  @ViewChild('arrow') arrow;
  public originalContent: string|SafeHtml;
  public content: string|SafeHtml;
  public maxWidth: string = '250px';
  public width = 250;
  public tooltipPosition: string = 'top';
  public copy: boolean = false;
  public copied: boolean = false;
  public isShown: boolean = false;
  public arrowPosition: string = 'bottom';
  public arrowLocation = {
    left : '-9999px',
    top : '-9999px'
  };
  public location: any = {
    left : '-9999px',
    top : '-9999px'
  };
  private _truncation: number;
  private _placement: string;

  constructor (
    private _tooltipService: TooltipService,
    private _detectorRef: ChangeDetectorRef
  ) {
    _tooltipService.event$.subscribe((tooltipEvent: OnOpeningTooltip|OnClosingTooltip|OnUpdateTooltip) => {
      if ( tooltipEvent instanceof OnOpeningTooltip ) {
        this.openTooltip(tooltipEvent);
      } else if ( tooltipEvent instanceof OnUpdateTooltip ) {
        this.updateTooltip(tooltipEvent.content);
      } else {
        this.closeTooltip();
      }
    });
  }

  /**
   * Copy the original content to the clipboard
   */
  public copyTooltip() {
    if ( this.copy ) {
      let virtualEl = document.createElement('textarea');
      virtualEl.value = this.originalContent as string;
      virtualEl.setAttribute('readonly', '');
      virtualEl.style.position = 'absolute';
      virtualEl.style.left = '-9999px';
      document.body.appendChild(virtualEl);
      virtualEl.select();
      document.execCommand('copy');
      document.body.removeChild(virtualEl);
      this.copied = true;
      this._detectorRef.markForCheck();
    }
  }

  /**
   * Position the tooltip to the left
   */
  private getTooltipLeft(position: any): {left: number; top: number} {
    let tooltipWidth = this.tooltip.nativeElement.getBoundingClientRect().width;
    let tooltipHeight = this.tooltip.nativeElement.getBoundingClientRect().height;
    let left = position.left - tooltipWidth - 20;
    let top = window.pageYOffset +  position.top + ( position.height / 2 ) - (tooltipHeight / 2) - 10;
    this.arrowPosition = 'right';
    this.arrowLocation = {
      top: Tools.numberToPixels((tooltipHeight / 2) + 5),
      left : Tools.numberToPixels(tooltipWidth + 10)
    };
    return {left, top};
  }

  /**
   * Position the tooltip to the bottom
   */
  private getTooltipBottom(position: any): {left: number; top: number} {
    let tooltipWidth = this.tooltip.nativeElement.getBoundingClientRect().width;
    let left = (position.left + ( position.width / 2)) - (tooltipWidth / 2) - 10;
    let top = window.pageYOffset +  position.top + position.height - 10;
    this.arrowPosition = 'top';
    this.arrowLocation = {
      top: Tools.numberToPixels(5),
      left : Tools.numberToPixels((tooltipWidth / 2) + 5)
    };
    return {left, top};
  }

  /**
   * Position the tooltip to the right
   */
  private getTooltipRight(position: any): {left: number; top: number} {
    let tooltipHeight = this.tooltip.nativeElement.getBoundingClientRect().height;
    let left = position.left + position.width - 5;
    let top = window.pageYOffset +  position.top + ( position.height / 2 ) - (tooltipHeight / 2) - 10;
    this.arrowPosition = 'left';
    this.arrowLocation = {
      top: Tools.numberToPixels((tooltipHeight / 2) + 5),
      left : Tools.numberToPixels(5)
    };
    return {left, top};
  }

  /**
   * Position the tooltip the the top
   */
  private getTooltipDefault(position: any): {left: number; top: number} {
    let tooltipWidth = this.tooltip.nativeElement.getBoundingClientRect().width;
    let tooltipHeight = this.tooltip.nativeElement.getBoundingClientRect().height;
    let left = (position.left + ( position.width / 2)) - (tooltipWidth / 2) - 10;
    let top = window.pageYOffset +  position.top - tooltipHeight - 15;
    this.arrowPosition = 'bottom';
    this.arrowLocation = {
      top: Tools.numberToPixels(tooltipHeight + 10),
      left : Tools.numberToPixels((tooltipWidth / 2) + 5)
    };
    return {left, top};
  }

  /**
   * Check the space available for the tooltip before positioning it
   */
  private setLocation(position: any) {
    if ( position ) {
      let viewportWidth = window.innerWidth;
      let viewportHeight = window.innerHeight;
      let tooltipWidth = this.tooltip.nativeElement.getBoundingClientRect().width;
      let tooltipHeight = this.tooltip.nativeElement.getBoundingClientRect().height;
      let tempPosition: {
        left: number;
        top: number;
      };
      switch ( this.tooltipPosition ) {
        case 'left' :
          tempPosition = this.getTooltipLeft(position);
          break;

        case 'right' :
          tempPosition = this.getTooltipRight(position);
          break;

        case 'bottom' :
          tempPosition = this.getTooltipBottom(position);
          break;

        default :
          tempPosition = this.getTooltipDefault(position);
      }

      if ( tempPosition.left < 0 ) {
        tempPosition.left = 5;
        this.arrowLocation.left = Tools.numberToPixels((position.left - tempPosition.left) + (position.width / 2) - 5);
      }

      if ( tempPosition.left + tooltipWidth > viewportWidth ) {
        tempPosition.left = viewportWidth - tooltipWidth - 5;
        this.arrowLocation.left = Tools.numberToPixels(position.left - tempPosition.left + (position.width / 2) - 5);
      }

      if ( tempPosition.top < 0 ) {
        tempPosition.top = window.pageYOffset + position.top + position.height + 5;
        this.arrowLocation.top = Tools.numberToPixels(5);
        this.arrowPosition = 'top';
      }

      if ( tempPosition.top > (viewportHeight + window.pageYOffset - tooltipHeight - 5) ) {
        tempPosition.top = window.pageYOffset +  position.top - tooltipHeight - 15;
        this.arrowLocation.top = Tools.numberToPixels(5);
        this.arrowPosition = 'bottom';
      }

      this.location = {
        left: Tools.numberToPixels(tempPosition.left),
        top: Tools.numberToPixels(tempPosition.top)
      };

      if ( Tools.valueExists(this.content) ) {
        this.isShown = true;
        this._detectorRef.markForCheck();
      } else {
        this.closeTooltip();
      }
    }
  }

  /**
   * Open the tooltip
   */
  private openTooltip(event: OnOpeningTooltip): void {
    if ( event.maxWidth ) {
      this.maxWidth = Tools.numberToPixels(event.maxWidth);
      this.width = event.maxWidth;
    }
    this.originalContent = event.content;
    this._truncation = event.truncation;
    this.content = this.truncateContent(this._truncation);
    this._detectorRef.markForCheck();
    this.tooltipPosition = event.position;
    this.copy = event.copy;
    this._placement = event.placement;
    setTimeout(() => this.setLocation(this._placement), 250);
  }

  /**
   * Update the content of the tooltip
   */
  private updateTooltip(content: string|SafeHtml): void {
    if ( this.isShown && this._placement ) {
      this.originalContent = content;
      this.content = this.truncateContent(this._truncation);
      this.setLocation(this._placement);
    }
  }

  /**
   * Truncate the original content
   */
  private truncateContent(truncation: number): string|SafeHtml {
    if ( !Tools.isDefined(truncation) ) {
      return this.originalContent;
    }

    return TruncatePipe.truncate(this.originalContent, 'middle', truncation);
  }

  /**
   * Close the tooltip
   */
  private closeTooltip(): void {
    this.isShown = false;
    this.content = '';
    this.copied = false;
    this.maxWidth = '250px';
    this.width = 250;
    this._truncation = undefined;
    this._placement = undefined;
    this.location = {
      left : '-9999px',
      top : '-9999px'
    };
    this._detectorRef.markForCheck();
  }
}
